import { motion } from 'framer-motion';
import { Mail, Linkedin, Github, Phone, Send, Loader2 } from 'lucide-react'; // Added Loader2
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useState } from 'react';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser'; // 1. Import EmailJS

export const ContactWindow = () => {
  const [isSubmitting, setIsSubmitting] = useState(false); // 2. Add loading state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Corrected keys to match your EmailJS Template {{name}} and {{email}}
    const templateParams = {
      name: formData.name,    // Changed from from_name
      email: formData.email,  // Changed from reply_to
      message: formData.message,
    };

    try {
      await emailjs.send(
        'service_inxnud8',
        'template_vel3x5t',
        templateParams,
        'QVVd0aXgwyn6NMot3'
      );

      toast.success('Message sent successfully! I\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'alvinjoregi76@gmail.com',
      link: 'mailto:alvinjoregi76@gmail.com',
      color: 'text-primary'
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: 'linkedin.com/in/alvinjoregi',
      link: 'https://linkedin.com/in/alvinjoregi',
      color: 'text-accent'
    },
    {
      icon: Phone, // Changed icon label to Instagram for clarity since value is IG
      label: 'Instagram',
      value: '@alvincodez',
      link: 'https://www.instagram.com/alvincodez/',
      color: 'text-primary'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl font-bold glow-text mb-2">Get In Touch</h1>
        <p className="text-muted-foreground">Let's discuss your next project or event</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-6 tech-border">
            <h3 className="text-xl font-bold text-primary mb-4">Send Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  required
                  disabled={isSubmitting}
                  className="tech-border"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  required
                  disabled={isSubmitting}
                  className="tech-border"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Message</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Your message..."
                  required
                  rows={6}
                  disabled={isSubmitting}
                  className="tech-border scrollbar-tech"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Card>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4">
          <Card className="p-6 tech-border">
            <h3 className="text-xl font-bold text-primary mb-4">Contact Information</h3>
            <div className="space-y-4">
              {contactInfo.map((info, i) => (
                <motion.a
                  key={info.label}
                  href={info.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.05 }}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group"
                >
                  <div className={`p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors`}>
                    <info.icon className={`w-5 h-5 ${info.color}`} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">{info.label}</div>
                    <div className="text-sm text-foreground group-hover:text-primary transition-colors">
                      {info.value}
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          </Card>

          <Card className="p-6 tech-border">
            <h3 className="text-xl font-bold text-primary mb-4">Work Status</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-success animate-pulse-glow" />
                <span className="text-sm text-foreground">Available for Event Organizing (Pinnacle)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary animate-pulse-glow" />
                <span className="text-sm text-foreground">Open to Full Stack Dev Projects</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-accent animate-pulse-glow" />
                <span className="text-sm text-foreground">Location: Sharjah/Dubai, UAE</span>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactWindow;